import { TextField } from '@mui/material';
import axios from 'axios';
import { cloneDeep } from 'lodash-es';
import Modal from 'molecules/Modal';
import {
  ModalActions,
  ModalCloseButton,
  ModalContent,
  ModalSubmitButton,
} from 'molecules/Modal/Modal.styles';
import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { UserState } from 'store/user/userSlice';

const textFieldStyles = {
  '& .MuiInputBase-root': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: '18px',
  },
  '& legend': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '12px',
    fontWeight: 400,
  },
};

const userFormDefaultValue = {
  firstName: { value: '', error: '' },
  lastName: { value: '', error: '' },
};

export const ChangeUserNameModal: FC<{
  open: boolean;
  user?: Partial<UserState>;
  onClose: () => void;
  onAfterFinish: () => void;
}> = ({ open, user, onClose, onAfterFinish }) => {
  const [userForm, setUserForm] = useState(userFormDefaultValue);

  const handleUserFormInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof typeof userForm
  ) => {
    const updatedForm = cloneDeep(userForm);
    const newValue = e.target.value;
    if (field === 'firstName') {
      if (newValue) {
        updatedForm.firstName.error = '';
      }
    } else if (field === 'lastName') {
      if (newValue) {
        updatedForm.lastName.error = '';
      }
    }
    updatedForm[field].value = newValue;
    setUserForm(updatedForm);
  };

  const handleSubmit = useCallback(() => {
    axios
      .put(`${process.env.REACT_APP_USER_SERVICE}/update`, {
        name: userForm.firstName.value,
        surname: userForm.lastName.value,
        id: user?.id,
      })
      .then(() => {
        onAfterFinish();
      })
      .catch(() => {
        onClose();
      });
  }, [userForm]);

  useEffect(() => {
    setUserForm({
      firstName: { value: user?.name || '', error: '' },
      lastName: { value: user?.surname || '', error: '' },
    });
  }, [user]);

  return (
    <Modal
      padding24
      open={open}
      maxWidth="md"
      title="Change user name"
      onClose={onClose}
    >
      <>
        <ModalContent>
          <TextField
            fullWidth
            size="small"
            autoComplete="given-name"
            margin="normal"
            label="First name"
            InputLabelProps={{
              shrink: true,
            }}
            value={userForm.firstName.value}
            error={!!userForm.firstName.error}
            helperText={userForm.firstName.error}
            onChange={(event) => handleUserFormInputChange(event, 'firstName')}
            sx={{ ...textFieldStyles }}
          />
          <TextField
            fullWidth
            size="small"
            autoComplete="family-name"
            margin="normal"
            label="Last name"
            InputLabelProps={{
              shrink: true,
            }}
            value={userForm.lastName.value}
            error={!!userForm.lastName.error}
            helperText={userForm.lastName.error}
            onChange={(event) => handleUserFormInputChange(event, 'lastName')}
            sx={{ ...textFieldStyles }}
          />
        </ModalContent>
        <ModalActions $padding24>
          <ModalCloseButton autoFocus variant="outlined" onClick={onClose}>
            Cancel
          </ModalCloseButton>
          <ModalSubmitButton variant="contained" onClick={handleSubmit}>
            Confirm
          </ModalSubmitButton>
        </ModalActions>
      </>
    </Modal>
  );
};
